import type { ReactNode } from "react";
import type { StyleLibrary } from "schemas/generated/project";

import React, { createContext } from "react";

import { useRequiredContext } from "replo-utils/react/context";
import { createStore, useStore } from "zustand";

interface SandboxStateStore {
  viewingVersionId: string | null;
  currentStyleLibrary: StyleLibrary | null;

  actions: {
    setViewingVersionId: (viewingVersionId: string | null) => void;
    setCurrentStyleLibrary: (currentStyleLibrary: StyleLibrary | null) => void;
    resetSandboxState: () => void;
  };
}

const createSandboxStateStore = () => {
  return createStore<SandboxStateStore>((set) => ({
    viewingVersionId: null,
    currentStyleLibrary: null,
    actions: {
      setViewingVersionId: (viewingVersionId) => set({ viewingVersionId }),
      setCurrentStyleLibrary: (currentStyleLibrary) =>
        set({ currentStyleLibrary }),
      resetSandboxState: () =>
        set({
          viewingVersionId: null,
          currentStyleLibrary: null,
        }),
    },
  }));
};

type SandboxStateContextType = {
  store: ReturnType<typeof createSandboxStateStore>;
};

const SandboxStateContext = createContext<SandboxStateContextType | null>(null);

const useSandboxStateContext = () => {
  return useRequiredContext(SandboxStateContext);
};

interface SandboxStateProviderProps {
  children: ReactNode;
}

// NOTE (Bobby, 2025-10-07): This provider is used to track the current sandbox state. The user may be viewing a temporary version of the sandbox so we
// need to make sure that temporary state is not overridden when we call the getPreviewUrl endpoint again. We call that endpoint continuously in order to keep
// the sandbox connected and we need to make sure to track these values so when we call getPreviewUrl we can tell the backend what the current state of the
// sandbox is and make sure we persist that state.
export function SandboxStateProvider({ children }: SandboxStateProviderProps) {
  const [store] = React.useState(() => createSandboxStateStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <SandboxStateContext.Provider value={contextValue}>
      {children}
    </SandboxStateContext.Provider>
  );
}

function useSandboxStateStore<SelectorReturnType>(
  selector: (state: SandboxStateStore) => SelectorReturnType,
): SelectorReturnType {
  const { store } = useSandboxStateContext();
  return useStore(store, selector);
}

export const useViewingVersionId = () =>
  useSandboxStateStore((state) => state.viewingVersionId);

export const useCurrentStyleLibrary = () =>
  useSandboxStateStore((state) => state.currentStyleLibrary);

export const useSandboxStateActions = () =>
  useSandboxStateStore((state) => state.actions);
